import {
  useMutation,
  type UseMutationResult,
  useQuery,
  useQueryClient,
  type UseQueryResult,
} from '@tanstack/react-query';
import { useCallback } from 'react';
import { useOrganisationFilter } from '@/features/organisations/providers/organisationHooks';
import { useProjectFilter } from '@/features/projects';
import {
  createRelease,
  deleteRelease,
  getReleaseBySlug,
  queryReleases,
  updateRelease,
} from '@/features/releases/data';
import {
  gridPaths,
  invalidateFeatures,
  invalidateGridQuery,
  invalidateReleaseDetail,
  invalidateReleaseSummaries,
  invalidateReleases,
  queryKeys,
  useInvalidateQueries,
  useIsAuthenticated,
  useOrganisationEntitlements,
} from '@/shared/hooks';
import { removeFromResult, upsertIntoResult } from '@/shared/hooks/shared';
import type { ApiError } from '@/shared/data/client';
import type { QueryResult, ReleaseDetailDto, ReleaseListItemDto } from '@flowqa/shared';

const toSlug = (value: string): string =>
  value
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-');

const toReleaseListItem = (release: ReleaseDetailDto): ReleaseListItemDto => ({
  id: release.id,
  slug: release.slug,
  name: release.name,
  status: release.status,
  plannedFor: release.plannedFor,
  releasedAt: release.releasedAt,
  projectId: release.projectId,
  project: release.project,
  features: release.features,
  suites: release.suites,
  tests: release.tests,
  latestSuiteResults: release.latestSuiteResults,
  latestTestResults: release.latestTestResults,
  tags: release.tags,
  deletedAt: release.deletedAt,
});

export const useReleasesQuery = (
  projectId?: string | null,
  options?: { enabled?: boolean },
): UseQueryResult<QueryResult<ReleaseListItemDto>, ApiError> => {
  const isAuthenticated = useIsAuthenticated();
  const { selectedOrganisationId } = useOrganisationFilter();
  const orgKey = selectedOrganisationId ?? null;
  const orgForQuery = selectedOrganisationId ?? undefined;
  const entitlementsQuery = useOrganisationEntitlements(selectedOrganisationId ?? null);
  const deliveryReady = selectedOrganisationId === null || entitlementsQuery.isSuccess;
  const deliveryEnabled =
    selectedOrganisationId === null
      ? true
      : (entitlementsQuery.data?.addOns.deliveryEnabled ?? false);
  const deliveryKey = deliveryEnabled ? 'delivery-on' : 'delivery-off';
  const isEnabled =
    isAuthenticated && (options?.enabled ?? true) && deliveryReady && deliveryEnabled;
  return useQuery<QueryResult<ReleaseListItemDto>, ApiError>({
    queryKey: queryKeys.releases(projectId, orgKey, deliveryKey),
    enabled: isEnabled,
    queryFn: async (): Promise<QueryResult<ReleaseListItemDto>> =>
      await queryReleases(projectId, orgForQuery, {
        page: 1,
        pageSize: 50,
        sorts: [{ field: 'createdAt', direction: 'desc' }],
        filters: [],
        include: {
          project: true,
          features: { ids: true },
          suites: { ids: true },
          tests: { ids: true },
          latestSuiteResults: { counts: true },
          latestTestResults: { counts: true },
        },
      }),
    staleTime: 30_000,
  });
};

export const useReleaseDetailQuery = (
  slug: string | null,
  projectId?: string | null,
): UseQueryResult<ReleaseDetailDto | null, ApiError> => {
  const isAuthenticated = useIsAuthenticated();
  const { selectedOrganisationId } = useOrganisationFilter();
  const { selectedProjectId } = useProjectFilter();
  const orgKey = selectedOrganisationId ?? null;
  const effectiveProjectId = projectId ?? selectedProjectId ?? null;
  const entitlementsQuery = useOrganisationEntitlements(selectedOrganisationId ?? null);
  const deliveryReady = selectedOrganisationId === null || entitlementsQuery.isSuccess;
  const deliveryEnabled =
    selectedOrganisationId === null
      ? true
      : (entitlementsQuery.data?.addOns.deliveryEnabled ?? false);
  const deliveryKey = deliveryEnabled ? 'delivery-on' : 'delivery-off';
  return useQuery<ReleaseDetailDto | null, ApiError>({
    queryKey: queryKeys.releaseDetail(slug, orgKey, effectiveProjectId, deliveryKey),
    enabled: isAuthenticated && Boolean(slug) && deliveryReady && deliveryEnabled,
    queryFn: async (): Promise<ReleaseDetailDto | null> =>
      await getReleaseBySlug(slug, effectiveProjectId, selectedOrganisationId ?? undefined),
    staleTime: 15_000,
  });
};

export const useCreateRelease = (): UseMutationResult<
  ReleaseDetailDto,
  ApiError,
  {
    name: string;
    projectId: string;
    status?: ReleaseDetailDto['status'];
    plannedFor?: string | null;
    releasedAt?: string | null;
    featureIds?: string[];
    suiteIds?: string[];
    testIds?: string[];
    tags?: string[];
  }
> => {
  const queryClient = useQueryClient();
  const invalidateQueries = useInvalidateQueries();
  const { selectedOrganisationId } = useOrganisationFilter();
  const orgKey = selectedOrganisationId ?? null;
  const allKey = queryKeys.releases(undefined, orgKey);
  const projectKey = (projectId: string): ReturnType<typeof queryKeys.releases> =>
    queryKeys.releases(projectId, orgKey);

  return useMutation<
    ReleaseDetailDto,
    ApiError,
    {
      name: string;
      projectId: string;
      status?: ReleaseDetailDto['status'];
      plannedFor?: string | null;
      releasedAt?: string | null;
      featureIds?: string[];
      suiteIds?: string[];
      testIds?: string[];
      tags?: string[];
    },
    {
      previousAll?: QueryResult<ReleaseListItemDto>;
      previousProject?: QueryResult<ReleaseListItemDto>;
      projectId: string;
      tempId: string;
    }
  >({
    mutationFn: async (input) => await createRelease(input, selectedOrganisationId),
    onMutate: (input) => {
      const tempId = `temp-${Date.now()}`;
      const optimistic: ReleaseListItemDto = {
        id: tempId,
        slug: toSlug(input.name),
        name: input.name.trim(),
        status: input.status ?? 'draft',
        plannedFor: input.plannedFor ? new Date(input.plannedFor) : null,
        releasedAt: input.releasedAt ? new Date(input.releasedAt) : null,
        projectId: input.projectId,
        features: { ids: input.featureIds ?? [] },
        suites: { ids: input.suiteIds ?? [] },
        tests: { ids: input.testIds ?? [] },
        deletedAt: null,
      };
      const previousAll = queryClient.getQueryData<QueryResult<ReleaseListItemDto>>(allKey);
      const previousProject = queryClient.getQueryData<QueryResult<ReleaseListItemDto>>(
        projectKey(input.projectId),
      );
      queryClient.setQueryData<QueryResult<ReleaseListItemDto>>(allKey, (prev) =>
        upsertIntoResult(prev, optimistic),
      );
      queryClient.setQueryData<QueryResult<ReleaseListItemDto>>(
        projectKey(input.projectId),
        (prev) => upsertIntoResult(prev, optimistic),
      );
      return { previousAll, previousProject, projectId: input.projectId, tempId };
    },
    onError: (_err, _vars, context) => {
      if (context?.previousAll) {
        queryClient.setQueryData(allKey, context.previousAll);
      }
      if (context?.previousProject) {
        queryClient.setQueryData(projectKey(context.projectId), context.previousProject);
      }
    },
    onSuccess: async (created, _vars, context) => {
      if (context.tempId) {
        queryClient.setQueryData<QueryResult<ReleaseListItemDto>>(allKey, (prev) =>
          upsertIntoResult(removeFromResult(prev, context.tempId), toReleaseListItem(created)),
        );
        queryClient.setQueryData<QueryResult<ReleaseListItemDto>>(
          projectKey(created.projectId),
          (prev) =>
            upsertIntoResult(removeFromResult(prev, context.tempId), toReleaseListItem(created)),
        );
      }
      await invalidateQueries([
        invalidateReleases(undefined, orgKey),
        invalidateReleaseDetail(null, orgKey, created.projectId),
        invalidateReleaseSummaries(),
        invalidateGridQuery(gridPaths.releases),
      ]);
    },
  });
};

export const useUpdateRelease = (): UseMutationResult<
  ReleaseDetailDto,
  ApiError,
  {
    id: string;
    name?: string;
    status?: ReleaseDetailDto['status'];
    plannedFor?: string | null;
    releasedAt?: string | null;
    featureIds?: string[];
    suiteIds?: string[];
    testIds?: string[];
    tags?: string[];
  },
  {
    previousAll?: QueryResult<ReleaseListItemDto>;
    previousProject?: QueryResult<ReleaseListItemDto>;
    projectId?: string;
  }
> => {
  const queryClient = useQueryClient();
  const invalidateQueries = useInvalidateQueries();
  const { selectedOrganisationId } = useOrganisationFilter();
  const orgKey = selectedOrganisationId ?? null;
  const allKey = queryKeys.releases(undefined, orgKey);
  const projectKey = (projectId: string | undefined): ReturnType<typeof queryKeys.releases> =>
    queryKeys.releases(projectId ?? null, orgKey);
  const resolveOptimisticUpdate = (input: {
    name?: string;
    status?: ReleaseDetailDto['status'];
    plannedFor?: string | null;
    releasedAt?: string | null;
    featureIds?: string[];
    suiteIds?: string[];
    testIds?: string[];
    tags?: string[];
  }): Partial<ReleaseListItemDto> => ({
    ...input,
    plannedFor: input.plannedFor ? new Date(input.plannedFor) : undefined,
    releasedAt: input.releasedAt ? new Date(input.releasedAt) : undefined,
  });

  return useMutation<
    ReleaseDetailDto,
    ApiError,
    {
      id: string;
      name?: string;
      status?: ReleaseDetailDto['status'];
      plannedFor?: string | null;
      releasedAt?: string | null;
      featureIds?: string[];
      suiteIds?: string[];
      testIds?: string[];
      tags?: string[];
    },
    {
      previousAll?: QueryResult<ReleaseListItemDto>;
      previousProject?: QueryResult<ReleaseListItemDto>;
      projectId?: string;
    }
  >({
    mutationFn: async (input) => await updateRelease(input, selectedOrganisationId),
    onMutate: (input) => {
      const previousAll = queryClient.getQueryData<QueryResult<ReleaseListItemDto>>(allKey);
      const projectId = previousAll?.items.find((item) => item.id === input.id)?.projectId;
      const previousProject = projectId
        ? queryClient.getQueryData<QueryResult<ReleaseListItemDto>>(projectKey(projectId))
        : undefined;
      const optimisticUpdate = resolveOptimisticUpdate(input);
      queryClient.setQueryData<QueryResult<ReleaseListItemDto>>(allKey, (prev) =>
        prev
          ? {
              ...prev,
              items: prev.items.map((item) =>
                item.id === input.id ? { ...item, ...optimisticUpdate } : item,
              ),
            }
          : prev,
      );
      if (previousProject) {
        queryClient.setQueryData<QueryResult<ReleaseListItemDto>>(projectKey(projectId), (prev) =>
          prev
            ? {
                ...prev,
                items: prev.items.map((item) =>
                  item.id === input.id ? { ...item, ...optimisticUpdate } : item,
                ),
              }
            : prev,
        );
      }
      return { previousAll, previousProject, projectId };
    },
    onError: (_err, _vars, context) => {
      if (context?.previousAll) {
        queryClient.setQueryData(allKey, context.previousAll);
      }
      if (context?.previousProject) {
        queryClient.setQueryData(projectKey(context.projectId), context.previousProject);
      }
    },
    onSuccess: async (updated) => {
      queryClient.setQueryData<QueryResult<ReleaseListItemDto>>(allKey, (prev) =>
        upsertIntoResult(prev, toReleaseListItem(updated)),
      );
      queryClient.setQueryData<QueryResult<ReleaseListItemDto>>(
        projectKey(updated.projectId),
        (prev) => upsertIntoResult(prev, toReleaseListItem(updated)),
      );
      await invalidateQueries([
        invalidateReleases(undefined, orgKey),
        invalidateReleases(updated.projectId, orgKey),
        invalidateReleaseDetail(updated.slug, orgKey, updated.projectId),
        invalidateGridQuery(gridPaths.releases),
      ]);
    },
  });
};

export const useDeleteRelease = (): UseMutationResult<
  { id: string; projectId?: string },
  ApiError,
  { id: string; projectId?: string },
  {
    previousAll?: QueryResult<ReleaseListItemDto>;
    previousProject?: QueryResult<ReleaseListItemDto>;
    projectId?: string;
  }
> => {
  const queryClient = useQueryClient();
  const invalidateQueries = useInvalidateQueries();
  const { selectedOrganisationId } = useOrganisationFilter();
  const orgKey = selectedOrganisationId ?? null;
  const allKey = queryKeys.releases(undefined, orgKey);
  const projectKey = (projectId: string | undefined): ReturnType<typeof queryKeys.releases> =>
    queryKeys.releases(projectId ?? null, orgKey);

  return useMutation<
    { id: string; projectId?: string },
    ApiError,
    { id: string; projectId?: string },
    {
      previousAll?: QueryResult<ReleaseListItemDto>;
      previousProject?: QueryResult<ReleaseListItemDto>;
      projectId?: string;
    }
  >({
    mutationFn: async (input) => {
      const result = await deleteRelease({ id: input.id }, selectedOrganisationId);
      return { ...result, projectId: input.projectId };
    },
    onMutate: (input) => {
      const previousAll = queryClient.getQueryData<QueryResult<ReleaseListItemDto>>(allKey);
      const previousProject = input.projectId
        ? queryClient.getQueryData<QueryResult<ReleaseListItemDto>>(projectKey(input.projectId))
        : undefined;
      queryClient.setQueryData<QueryResult<ReleaseListItemDto>>(allKey, (prev) =>
        removeFromResult(prev, input.id),
      );
      if (input.projectId) {
        queryClient.setQueryData<QueryResult<ReleaseListItemDto>>(
          projectKey(input.projectId),
          (prev) => removeFromResult(prev, input.id),
        );
      }
      return { previousAll, previousProject, projectId: input.projectId };
    },
    onError: (_err, _vars, context) => {
      if (context?.previousAll) {
        queryClient.setQueryData(allKey, context.previousAll);
      }
      if (context?.previousProject) {
        queryClient.setQueryData(projectKey(context.projectId), context.previousProject);
      }
    },
    onSuccess: async (deleted) => {
      queryClient.setQueryData<QueryResult<ReleaseListItemDto>>(allKey, (prev) =>
        removeFromResult(prev, deleted.id),
      );
      if (deleted.projectId) {
        queryClient.setQueryData<QueryResult<ReleaseListItemDto>>(
          projectKey(deleted.projectId),
          (prev) => removeFromResult(prev, deleted.id),
        );
      }
      await invalidateQueries([
        invalidateReleases(undefined, orgKey),
        ...(deleted.projectId ? [invalidateReleases(deleted.projectId, orgKey)] : []),
        ...(deleted.projectId ? [invalidateFeatures(deleted.projectId, orgKey)] : []),
        invalidateGridQuery(gridPaths.releases),
        invalidateGridQuery(gridPaths.features),
      ]);
    },
  });
};

export const useInvalidateReleases = (): ((projectId?: string | null) => Promise<void>) => {
  const invalidateQueries = useInvalidateQueries();
  const { selectedOrganisationId } = useOrganisationFilter();
  const orgKey = selectedOrganisationId ?? null;
  return useCallback(
    async (projectId?: string | null): Promise<void> => {
      await invalidateQueries([
        invalidateReleases(projectId, orgKey),
        invalidateGridQuery(gridPaths.releases),
      ]);
    },
    [invalidateQueries, orgKey],
  );
};

export const useInvalidateReleaseDetail = (): ((
  slug: string | null,
  projectId?: string | null,
) => Promise<void>) => {
  const invalidateQueries = useInvalidateQueries();
  const { selectedOrganisationId } = useOrganisationFilter();
  const { selectedProjectId } = useProjectFilter();
  const orgKey = selectedOrganisationId ?? null;
  return useCallback(
    async (slug: string | null, projectId?: string | null): Promise<void> => {
      await invalidateQueries([
        invalidateReleaseDetail(slug, orgKey, projectId ?? selectedProjectId ?? null),
      ]);
    },
    [invalidateQueries, orgKey, selectedProjectId],
  );
};
